import { AlertTriangle, ArrowLeft, Printer, Send } from 'lucide-react';
import { proposalsApi } from '@/api/calculators';
import { ConvertToProposalButton } from './_shared/ConvertToProposalButton';
import { LeadField } from './pool/PoolUi';
import { ForwardFlow } from './pool/ForwardFlow';
import { ForwardResultsTabs, RecommendationView } from './pool/ForwardResults';
import { RoiPageView } from './pool/RoiPageView';
import { ReverseFlow } from './pool/ReverseFlow';
import { ReverseResultView } from './pool/ReverseResults';
import { usePoolCalculator } from './pool/usePoolCalculator';

export function PoolCalculatorPage() {
  const { calculatedInput, inputMode, setInputMode, volume, setVolume, volumeUnit, changeVolumeUnit, navigate, session, setSession, tab, setTab, climate, setClimate, unit, setUnit, length, setLength, width, setWidth, depthAvg, setDepthAvg, placement, setPlacement, application, setApplication, winterOp, setWinterOp, heatTimeTarget, setHeatTimeTarget, targetTemp, setTargetTemp, revModel, setRevModel, revClimate, setRevClimate, revPlacement, setRevPlacement, revWinter, setRevWinter, revQty, setRevQty, revLength, setRevLength, revWidth, setRevWidth, revDepth, setRevDepth, revUnit, setRevUnit, forwardResult, setForwardResult, reverseResult, setReverseResult, forwardSubTab, setForwardSubTab, error, setError, loading, setLoading, leadName, setLeadName, leadPhone, setLeadPhone, leadEmail, setLeadEmail, leadCity, setLeadCity, leadNotes, setLeadNotes, savedQuotationId, setSavedQuotationId, savingQuote, setSavingQuote, sendingLead, setSendingLead, leadSent, setLeadSent, handleCalculateForward, handleCalculateReverse, handleSaveQuotation, handleSendLead, handlePrint } = usePoolCalculator();
  if (!session) {
    return (
      <div className="mx-auto max-w-2xl p-6 mt-10 text-center">
        <AlertTriangle size={32} className="mx-auto mb-3 text-amber-600" />
        <h2 className="text-lg font-semibold text-slate-900">
          No active meeting session
        </h2>
        <p className="mt-1 text-slate-600">
          The Pool Heat Pump calculator is only available inside a Meeting
          Mode session. Start one to bind the sizing to a lead.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sales/meeting/start')}
          className="mt-4 rounded-md bg-slate-900 px-5 py-2 text-white hover:bg-slate-800"
        >
          Initiate Meeting Mode
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl">
        {/* Back link */}
        <div className="mb-3 flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={() => navigate('/sales/meeting/calculators')}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} /> Choose another calculator
          </button>
        </div>

        {/* Header */}
        <div className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Sun Stellar Pool Heat Pump Calculator
          </h1>
          <p className="mt-1 text-xs text-slate-600">
            Forward sizing (Calculate &amp; Recommend) or reverse mode
            (Select Model Directly).
          </p>
        </div>

        {/* Tab strip */}
        <div
          role="tablist"
          aria-label="Pool calculator mode"
          className="mb-4 flex gap-1 rounded-lg bg-stone-200 p-1 print:hidden"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'forward'}
            data-testid="pool-tab-forward"
            onClick={() => setTab('forward')}
            className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
              tab === 'forward'
                ? 'bg-white font-bold text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div>Calculate &amp; Recommend</div>
            <div className="mt-0.5 text-[11px] font-normal text-slate-500">
              Forward · enter dimensions or volume
            </div>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'reverse'}
            data-testid="pool-tab-reverse"
            onClick={() => setTab('reverse')}
            className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
              tab === 'reverse'
                ? 'bg-white font-bold text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div>Select Model Directly</div>
            <div className="mt-0.5 text-[11px] font-normal text-slate-500">
              Reverse · max pool size for a SKU
            </div>
          </button>
        </div>

        {error && (
          <div
            role="alert"
            data-testid="pool-error"
            className="mb-3 rounded-md border-l-4 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-800 print:hidden"
          >
            {error}
          </div>
        )}

        {tab === 'forward' && (
          <ForwardFlow
            climate={climate}
            setClimate={setClimate}
            unit={unit}
            setUnit={setUnit}
            inputMode={inputMode}
            setInputMode={setInputMode}
            volume={volume}
            setVolume={setVolume}
            volumeUnit={volumeUnit}
            setVolumeUnit={changeVolumeUnit}
            length={length}
            setLength={setLength}
            width={width}
            setWidth={setWidth}
            depthAvg={depthAvg}
            setDepthAvg={setDepthAvg}
            placement={placement}
            setPlacement={setPlacement}
            application={application}
            setApplication={setApplication}
            winterOp={winterOp}
            setWinterOp={setWinterOp}
            heatTimeTarget={heatTimeTarget}
            setHeatTimeTarget={setHeatTimeTarget}
            targetTemp={targetTemp}
            setTargetTemp={setTargetTemp}
            loading={loading}
            onCalculate={handleCalculateForward}
          />
        )}

        {tab === 'forward' && forwardResult && calculatedInput && (
          <div id="pool-results" data-testid="pool-results" className="mt-4">
            <ForwardResultsTabs
              subTab={forwardSubTab}
              setSubTab={setForwardSubTab}
            />
            {forwardSubTab === 'rec' && (
              <RecommendationView
                response={forwardResult}
                placement={calculatedInput.placement}
                application={calculatedInput.application}
                targetTemp={calculatedInput.target_temp}
                heatTimeTarget={calculatedInput.heat_time_target}
                winterOp={calculatedInput.winter_operation ? 'yes' : 'no'}
              />
            )}
            {forwardSubTab === 'roi' && (
              <RoiPageView response={forwardResult} application={calculatedInput.application} />
            )}

            {/* Lead form */}
            <div
              className="mt-4 rounded-lg bg-white p-5 shadow-sm print:hidden"
              data-testid="pool-lead"
            >
              <h3 className="text-sm font-semibold text-slate-900">
                Get a detailed quote
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                Share your details and our team will send a customised quote
                with this sizing + ROI page attached.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <LeadField
                  label="Full name *"
                  value={leadName}
                  onChange={setLeadName}
                  placeholder="Your name"
                  testId="pool-lead-name"
                />
                <LeadField
                  label="Mobile number *"
                  value={leadPhone}
                  onChange={setLeadPhone}
                  placeholder="+91 98XXXXXXXX"
                  type="tel"
                  testId="pool-lead-phone"
                />
                <LeadField
                  label="Email address"
                  value={leadEmail}
                  onChange={setLeadEmail}
                  placeholder="you@example.com"
                  type="email"
                />
                <LeadField
                  label="City"
                  value={leadCity}
                  onChange={setLeadCity}
                  placeholder="Delhi, Mumbai…"
                />
              </div>
              <div className="mt-2.5">
                <LeadField
                  label="Any specific requirement or question?"
                  value={leadNotes}
                  onChange={setLeadNotes}
                  placeholder="Optional notes…"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSendLead}
                  disabled={sendingLead || leadSent}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                  data-testid="pool-send-lead"
                >
                  <Send size={14} />
                  {leadSent ? 'Enquiry sent' : sendingLead ? 'Sending…' : 'Send enquiry →'}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="pool-print"
                >
                  <Printer size={14} /> Print / Save as PDF
                </button>
              </div>
              {leadSent && (
                <div
                  className="mt-3 rounded-md border-l-4 border-emerald-600 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
                  data-testid="pool-lead-confirm"
                >
                  ✓ Enquiry sent! Our team will contact you within 24 hours.
                </div>
              )}
            </div>

            {/* AURA — save & convert */}
            <div className="mt-3 rounded-lg bg-white p-5 shadow-sm print:hidden">
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                AURA — save &amp; convert
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveQuotation}
                  disabled={savingQuote || !!savedQuotationId}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="pool-save-quote"
                >
                  {savedQuotationId
                    ? 'Quotation saved'
                    : savingQuote
                      ? 'Saving…'
                      : 'Save Quotation'}
                </button>
                {savedQuotationId && (
                  <ConvertToProposalButton
                    calculatorKey="pool_heat_pump"
                    quotationId={savedQuotationId}
                    customerName={session.leadLabel ?? undefined}
                    leadId={session.leadId ?? undefined}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'reverse' && (
          <ReverseFlow
            model={revModel}
            setModel={setRevModel}
            climate={revClimate}
            setClimate={setRevClimate}
            placement={revPlacement}
            setPlacement={setRevPlacement}
            winterOp={revWinter}
            setWinterOp={setRevWinter}
            qty={revQty}
            setQty={setRevQty}
            length={revLength}
            setLength={setRevLength}
            width={revWidth}
            setWidth={setRevWidth}
            depthAvg={revDepth}
            setDepthAvg={setRevDepth}
            unit={revUnit}
            setUnit={setRevUnit}
            loading={loading}
            onCalculate={handleCalculateReverse}
          />
        )}

        {tab === 'reverse' && reverseResult && (
          <div
            id="pool-reverse-result"
            data-testid="pool-reverse-result"
            className="mt-4"
          >
            <ReverseResultView result={reverseResult} />
          </div>
        )}

        <div className="mt-4 text-center text-xs text-slate-400 print:hidden">
          Sun Stellar Pool Heat Pump · Bapauli, Haryana · Noida · Delhi
        </div>
      </div>
      <span className="sr-only">{typeof proposalsApi}</span>

      {/* Print stylesheet — split forward result into Page 1 (Rec) and
          Page 2 (ROI). The two views are conditionally rendered in the
          DOM (only one at a time during interactive use), but for print
          we force both to render and use a hard page break between them. */}
      <style>{`
        @media print {
          .pool-print-roi-page { break-before: page; }
        }
      `}</style>
    </div>
  );
}


export default PoolCalculatorPage;
